import type { Action } from "@reduxjs/toolkit";

const issueEndpointNames = new Set(["issuePersonalAccessToken", "issueAdminPersonalAccessToken"]);

type RecordValue = Record<string, unknown>;

const isRecord = (value: unknown): value is RecordValue =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const withoutIssuedSecret = (value: unknown): unknown => {
  if (!isRecord(value) || !Object.hasOwn(value, "secret")) return value;

  const sanitized = { ...value };
  delete sanitized.secret;
  return sanitized;
};

export const createPersonalAccessTokenDevToolsSanitizers = (apiReducerPath: string) => ({
  actionSanitizer: <A extends Action>(action: A, _id: number): A => {
    const actionData = action as A & {
      meta?: { arg?: { endpointName?: unknown } };
      payload?: unknown;
    };
    const isIssuedTokenAction =
      action.type === `${apiReducerPath}/executeMutation/fulfilled` &&
      typeof actionData.meta?.arg?.endpointName === "string" &&
      issueEndpointNames.has(actionData.meta.arg.endpointName);
    if (!isIssuedTokenAction) return action;

    const payload = withoutIssuedSecret(actionData.payload);
    if (payload === actionData.payload) return action;

    return { ...actionData, payload } as A;
  },
  stateSanitizer: <S>(state: S, _index: number): S => {
    if (!isRecord(state)) return state;

    const apiState = state[apiReducerPath];
    if (!isRecord(apiState) || !isRecord(apiState.mutations)) return state;

    let changed = false;
    const mutations = { ...apiState.mutations };
    for (const [requestId, mutation] of Object.entries(apiState.mutations)) {
      if (!isRecord(mutation) || typeof mutation.endpointName !== "string") continue;
      if (!issueEndpointNames.has(mutation.endpointName)) continue;

      const data = withoutIssuedSecret(mutation.data);
      if (data === mutation.data) continue;

      mutations[requestId] = { ...mutation, data };
      changed = true;
    }

    if (!changed) return state;

    return {
      ...state,
      [apiReducerPath]: { ...apiState, mutations },
    } as S;
  },
});
